export type EntityId = string;
export type UnixTimestamp = number;
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export type ItemKind = "document" | "canvas";

export interface VersionedEntity {
  id: EntityId;
  schemaVersion: number;
  revision: number;
  createdAt: UnixTimestamp;
  updatedAt: UnixTimestamp;
  deletedAt?: UnixTimestamp;
}

export interface KnowledgeItem extends VersionedEntity {
  kind: ItemKind;
  title: string;
  icon?: string;
  folderId?: EntityId;
  tags: EntityId[];
  properties: Record<string, JsonValue>;
}

export interface DomainBlock {
  id: EntityId;
  type: string;
  props: Record<string, JsonValue>;
  content: JsonValue;
  children: DomainBlock[];
}

export interface DomainDocument {
  schemaVersion: number;
  blocks: DomainBlock[];
}

export type ReferenceTarget =
  | { kind: "item"; id: EntityId }
  | { kind: "block"; id: EntityId }
  | { kind: "attachment"; id: EntityId };

/**
 * Stable application-level references stored in rich-text links.
 *
 * Resolvable references are authoritative; preview URLs (blob:, file:, signed
 * HTTPS URLs, and similar values) are deliberately kept out of persisted
 * document content.
 */
export function createReferenceUri(target: ReferenceTarget): string {
  if (target.id.length === 0) {
    throw new TypeError("Reference target ID must not be empty");
  }
  return `note://${target.kind}/${encodeURIComponent(target.id)}`;
}

export function parseReferenceUri(value: string): ReferenceTarget | null {
  const match = /^note:\/\/(item|block|attachment)\/(.+)$/u.exec(value);
  if (!match?.[1] || !match[2]) {
    return null;
  }
  try {
    const id = decodeURIComponent(match[2]);
    return id.length > 0
      ? { kind: match[1] as ReferenceTarget["kind"], id }
      : null;
  } catch {
    return null;
  }
}

export type CanvasMode = "freeform" | "mindmap" | "research" | "project";

export interface CanvasGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: string;
}

export interface CanvasNode {
  id: EntityId;
  type: string;
  geometry: CanvasGeometry;
  props: Record<string, JsonValue>;
  reference?: ReferenceTarget;
  parentFrameId?: EntityId;
  readingOrder?: number;
}

export interface CanvasEdge {
  id: EntityId;
  sourceNodeId: EntityId;
  targetNodeId: EntityId;
  relationType?: string;
  label?: string;
  props: Record<string, JsonValue>;
}

export interface DomainCanvas {
  schemaVersion: number;
  mode: CanvasMode;
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  settings: Record<string, JsonValue>;
}

export interface AttachmentMetadata extends VersionedEntity {
  /**
   * Stable attachment identifier. `id` remains the VersionedEntity-compatible
   * alias while storage and archive boundaries use the explicit name.
   */
  attachmentId?: EntityId;
  sha256: string;
  mimeType: string;
  byteLength: number;
  originalName: string;
  metadata: Record<string, JsonValue>;
}

export interface SaveReceipt {
  entityId: EntityId;
  revision: number;
  committedAt: UnixTimestamp;
}

export interface PageRequest {
  cursor?: string;
  limit?: number;
}

export interface PageResult<T> {
  items: T[];
  nextCursor?: string;
}

/**
 * The single content-schema clock shared by documents, canvases and
 * versioned knowledge-base entities. Database migrations use an independent
 * version sequence.
 */
export const EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION = 1;
export const CURRENT_CONTENT_SCHEMA_VERSION = 2;

export type ContentSchemaCompatibility =
  | "current"
  | "migratable"
  | "unsupported-old"
  | "future";

export interface ContentSchemaMigration<T> {
  readonly from: number;
  readonly to: number;
  migrate(value: T): T;
}

export interface ContentSchemaMigrationResult<T> {
  readonly value: T;
  readonly migratedFrom?: number;
}

export function classifyContentSchemaVersion(
  schemaVersion: number,
): ContentSchemaCompatibility {
  if (schemaVersion === CURRENT_CONTENT_SCHEMA_VERSION) {
    return "current";
  }
  if (
    Number.isSafeInteger(schemaVersion) &&
    schemaVersion >= EARLIEST_SUPPORTED_CONTENT_SCHEMA_VERSION &&
    schemaVersion < CURRENT_CONTENT_SCHEMA_VERSION
  ) {
    return "migratable";
  }
  return schemaVersion > CURRENT_CONTENT_SCHEMA_VERSION
    ? "future"
    : "unsupported-old";
}

/**
 * Applies a contiguous migration chain without knowing the content shape.
 * Packages own shape-specific steps while sharing chain safety here.
 */
export function migrateContentSchema<T extends { schemaVersion: number }>(
  value: T,
  migrations: readonly ContentSchemaMigration<T>[],
): ContentSchemaMigrationResult<T> {
  const originalVersion = value.schemaVersion;
  const compatibility = classifyContentSchemaVersion(originalVersion);
  if (compatibility === "current") {
    return { value };
  }
  if (compatibility !== "migratable") {
    throw new RangeError(
      `Content schema version ${originalVersion} cannot be migrated to ${CURRENT_CONTENT_SCHEMA_VERSION}`,
    );
  }

  const bySource = new Map(migrations.map((migration) => [
    migration.from,
    migration,
  ]));
  let migrated = value;
  while (migrated.schemaVersion < CURRENT_CONTENT_SCHEMA_VERSION) {
    const step = bySource.get(migrated.schemaVersion);
    if (
      step === undefined ||
      step.to !== step.from + 1 ||
      step.to > CURRENT_CONTENT_SCHEMA_VERSION
    ) {
      throw new RangeError(
        `Missing contiguous content migration from schema ${migrated.schemaVersion}`,
      );
    }
    migrated = step.migrate(migrated);
    if (migrated.schemaVersion !== step.to) {
      throw new RangeError(
        `Content migration ${step.from} -> ${step.to} returned schema ${migrated.schemaVersion}`,
      );
    }
  }
  return { value: migrated, migratedFrom: originalVersion };
}
